import { ClockIcon } from '@heroicons/react/24/outline'
import { IVisiblePropActionButton } from 'config'
import { API_LOAN_TRIGGER_ACTION_BUTTON } from 'config/api.url.constants'
import { usePermissions } from 'hooks/usePermissions'
import { ReactNode, useState } from 'react'
import { useSelector } from 'react-redux'
import Api from 'services/api'

import { Button } from '../Button/Button'

export const triggerActionButton = (
  actionType: string,
  actionKey: string,
  loanNumber?: number,
): Promise<{ success: boolean; message?: string }> => {
  const options: any = loanNumber ? { loanNumber } : {}
  return Api.post(API_LOAN_TRIGGER_ACTION_BUTTON, { actionType, actionKey }, {}, options)
}

export interface ActionButtonProps {
  field: IVisiblePropActionButton
  value?: Record<string, any>
  onChange?: (value: any) => any
  onBlur?: () => any
  showHistory?: () => boolean
  loading?: boolean
  disabled?: boolean
  /**
   * Optional custom content (icons, layout) to render inside the button.
   * Defaults to `field.title` when not provided.
   */
  children?: ReactNode
  /** Optional overrides for the underlying Button's presentation. */
  color?: string
  className?: string
  full?: boolean
  additionalElements?: JSX.Element | null
}

export const ActionButton = ({
  field,
  value,
  onChange,
  onBlur,
  showHistory,
  loading,
  disabled,
  children,
  color,
  className,
  full,
  additionalElements,
}: ActionButtonProps) => {
  const { buttonStyle = 'primary', actionType, actionKey, title, clickMode = 'always' } = field as any
  const [running, setRunning] = useState(false)

  if (field.visible === false) return null
  if (!onChange && (!actionType || !actionKey)) return null

  const { hasPermission } = usePermissions()

  const {
    profile: { email },
  } = useSelector((state: any) => ({
    profile: state.auth.profile,
  }))

  const isClickedOnce = clickMode === 'once' && !!value
  const canReset = hasPermission ? !!hasPermission('Loan.CustomAction') : false

  // Once-mode, already clicked, no admin-reset permission: hide the button from ordinary users.
  if (isClickedOnce && !canReset) return null

  const handleClick = async () => {
    if (running) return
    setRunning(true)

    const value = {
      clickedAt: new Date().toISOString(),
      clickedBy: email,
    }

    if (onChange) await onChange(value)
    if (onBlur) await onBlur()

    await triggerActionButton(actionType!, actionKey!)

    setRunning(false)
  }

  const handleMakeLive = async () => {
    if (running) return
    setRunning(true)
    if (onChange) await onChange(null)
    if (onBlur) await onBlur()
    setRunning(false)
  }

  const mainButton = (
    <Button
      onClick={handleClick}
      outline={buttonStyle === 'outline'}
      link={buttonStyle === 'link'}
      loading={loading || running}
      disabled={disabled || (isClickedOnce && canReset)}
      color={color}
      className={className}
      full={full}
    >
      {children ?? title ?? ''}
    </Button>
  )

  const historyIcon = showHistory && (
    <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
      <ClockIcon className="h-[14px] w-[14px] text-gray-600 cursor-pointer" aria-hidden="true" />
    </span>
  )

  return (
    <div className="group flex flex-col items-start gap-1">
      <div className="flex items-center">
        {mainButton}
        {historyIcon}
        {additionalElements}
      </div>
      {isClickedOnce && canReset && (
        <div className="-mt-4">
          <Button onClick={handleMakeLive} link loading={loading || running} disabled={disabled} color={color}>
            Make Live
          </Button>
        </div>
      )}
    </div>
  )
}
